import {
  DraftStatus,
  SetDraftResponse,
  SetLoadResponse,
  UploadTrayReferenceImageForDraftResponse,
} from 'api/Responses/SetResponses';
import axiosInstance from 'services/APIService';

const ENDPOINT_PATH = `/v1/sets`;
const ENDPOINT_PATH_V2 = `/v2/sets`;

/*
 * Loads a set from the database into a draft bucket
 */
export const load = async (code: string): Promise<SetLoadResponse> => {
  const response = await axiosInstance.get<SetLoadResponse>(`${ENDPOINT_PATH}/set/${code}`);
  return response.data;
};

/*
 * Gets a draft from the draft bucket
 */
export const get = async (id: string): Promise<SetDraftResponse> => {
  const response = await axiosInstance.get<SetDraftResponse>(`${ENDPOINT_PATH}/draft/${id}`);
  return response.data;
};

/*
 * Gets a draft from the draft bucket V2
 */
export const getV2 = async (id: string): Promise<SetDraftResponse> => {
  const response = await axiosInstance.get<SetDraftResponse>(`${ENDPOINT_PATH_V2}/${id}`);
  return response.data;
};

/*
 * Saves a draft
 */
export const save = async (id: string, draft: SetDraftResponse): Promise<void> => {
  await axiosInstance.post(`${ENDPOINT_PATH}/draft/${id}`, draft);
};

/*
 * Validates a draft
 */
export const validate = async (id: string): Promise<void> => {
  await axiosInstance.get(`${ENDPOINT_PATH}/draft/validate/${id}`);
};

/*
 * Submits a draft for review/merge (using status param)
 */
export const submit = async (id: string, status: DraftStatus): Promise<void> => {
  await axiosInstance.post(`${ENDPOINT_PATH}/submit_draft/${id}/${status}`);
};

/*
 * Merges a draft into the database
 */
export const merge = async (id: string): Promise<void> => {
  await axiosInstance.put(`${ENDPOINT_PATH}/draft/${id}`);
};

/*
 * Deletes a draft from the draft bucket
 */
export const remove = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${ENDPOINT_PATH}/draft/${id}`);
};

/*
 * Lists all of the users current drafts
 */
export const list = async (): Promise<string[]> => {
  const response = await axiosInstance.get<string[]>(`${ENDPOINT_PATH}/draft_list`);
  return response.data;
};

/*
 * Lists all of the currently pending drafts
 * Can be either in review, or review for merge, depending on the status param
 */
export const listPending = async (status: DraftStatus): Promise<string[]> => {
  const response = await axiosInstance.get<string[]>(`${ENDPOINT_PATH}/pending_drafts/${status}`);
  return response.data;
};

export const getDraftPDF = async (id: string): Promise<string> => {
  const response = await axiosInstance.get<string>(`${ENDPOINT_PATH}/draft/pdf/${id}`, { responseType: 'blob' });
  return response.data;
};

export const getSetPDF = async (id: string): Promise<string> => {
  const response = await axiosInstance.get<string>(`${ENDPOINT_PATH}/set/pdf/${id}`, { responseType: 'blob' });
  return response.data;
};

/*
 * Get the tray reference image from the draft bucket
 */
export const getTrayReferenceImageForDraft = async (setCode: string, trayRef: string): Promise<string> => {
  const response = await axiosInstance.get<string>(`${ENDPOINT_PATH_V2}/reference-image/${setCode}/${trayRef}/`, {
    responseType: 'blob',
  });
  try {
    return URL.createObjectURL(new Blob([response.data]));
  } catch (e) {
    return response.data;
  }
};

/*
 * Uploads an image to the draft bucket
 */
export const uploadTrayReferenceImageForDraft = async (
  setCode: string,
  image: File,
): Promise<UploadTrayReferenceImageForDraftResponse> => {
  const data = new FormData();
  data.append('image', image, image.name);
  const response = await axiosInstance.put<UploadTrayReferenceImageForDraftResponse>(
    `${ENDPOINT_PATH_V2}/reference-image/${setCode}/`,
    data,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};
